// Source of truth for the careers page. Add, edit, or remove jobs here.
// `slug` becomes the URL: /careers/[slug]

export const CAREERS_INTRO =
  "Elementix is the leading data analytics platform for private real estate lenders and investors. We map people to their real estate activity, turning fragmented public records into a single queryable source of truth to understand activity and credit risk.";

// Inline segments allow partial bold/italic within paragraphs and list items.
export type InlineSegment = { text: string; b?: boolean; i?: boolean };
export type RichText = string | InlineSegment[];

export type JobBlock =
  | { type: 'heading'; text: string }
  | { type: 'paragraph'; text: RichText }
  | { type: 'list'; items: RichText[] };

export type Job = {
  slug: string;
  title: string;
  meta?: string[];
  description: JobBlock[];
  applyUrl: string;
};

export const JOBS: Job[] = [
  /*
  {
    slug: 'account-executive-smb',
    title: 'Account Executive, SMB',
    meta: ['Remote (US)', 'Full-time', '$120k OTE'],
    applyUrl: 'https://www.linkedin.com/jobs/view/4418980285/',
    description: [
      { type: 'heading', text: 'About Elementix' },
      {
        type: 'paragraph',
        text: 'Elementix is the data platform for private lenders. We help lenders originate new business, underwrite credit risk, and monitor existing borrowers by turning public records into a workflow. Founded in 2024, we are a small, AI-first team growing through strong inbound demand from a focused B2B SaaS motion.',
      },
      { type: 'heading', text: 'About the role' },
      {
        type: 'paragraph',
        text: [
          {
            text: "As an Account Executive you'll play a key role in every aspect of the sales cycle. You will qualify inbound leads, give demos, set up trials, and close deals, all from day one. With a disruptive and differentiated product driving significant inbound demand, you'll be assigned high-quality leads eager to evaluate and purchase. ",
          },
          { text: 'On target earnings $120,000/year.', b: true },
        ],
      },
      {
        type: 'paragraph',
        text: 'A clear picture of what a typical week looks like:',
      },
      {
        type: 'list',
        items: [
          [{ text: 'Around 50 qualified inbound leads per month, no outbound prospecting', b: true }],
          [{ text: '5 to 10 new opportunities created per week', b: true }],
          [{ text: 'Average deal cycle is 2 weeks from first call to signed contract', b: true }],
          [{ text: 'Deals go through a free trial period', b: true }],
          'Fewer than 20 active opportunities at any given time that need real attention',
          'Defined sales process with documented stages, plays, and templates, with room to refine',
        ],
      },
      { type: 'heading', text: 'Responsibilities' },
      {
        type: 'list',
        items: [
          'Use AI tooling to compress every part of your day',
          'Qualify inbound leads through discovery and a workflow-tailored product walkthrough, then book the next step right away',
          'Move deals through a defined stage gate process and keep the CRM clean every day',
          'Send concise, useful follow-up email, including recaps, mutual action plans, and quotes',
          'Forecast weekly with realistic confidence levels',
          'Onboard new customers after close, get them to first value in the product, and hand off cleanly',
          'Surface product and pricing feedback from lost deals and trial users back to the engineering team',
        ],
      },
      { type: 'heading', text: 'Qualifications' },
      {
        type: 'list',
        items: [
          '2 to 4 years carrying a quota in SMB B2B SaaS, ideally with deal sizes between $10k and $50k',
          'A process-oriented operator who thrives inside a defined sales cycle and improves it as they run it',
          [
            {
              text: 'AI-fluent: you already use Claude or similar in your sales workflow and have opinions on what works',
              b: true,
            },
          ],
          'Strong written communication, your follow-up emails carry weight in a 2-week cycle with light touch',
          'Comfortable taking 5 to 10 calls a day from warm inbound and qualifying fast',
        ],
      },
      { type: 'heading', text: 'Nice to Haves' },
      {
        type: 'list',
        items: [
          'Background in fintech, proptech, or private lending',
          'Experience as the first or second AE at a startup, used to ambiguity and ownership',
          'Familiarity building or refining sales playbooks and sequences',
        ],
      },
      {
        type: 'paragraph',
        text: [
          {
            text: 'This is a remote US role, full time. We are an equal opportunity employer.',
            b: true,
            i: true,
          },
        ],
      },
    ],
  },
  */
];

export const HAS_OPEN_ROLES = JOBS.length > 0;

export function getJob(slug: string): Job | undefined {
  return JOBS.find((j) => j.slug === slug);
}
